import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate } from "react-router-dom";
import "./addRewardCoupon.css";
import { addNewRewardCoupon } from "../../Redux/Actions/rewardCouponActions";
import { AgentList } from "../../Redux/Actions/agentActions";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

const AddRewardCoupon = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading = false } = useSelector((state) => state.rewardCoupon || {});
  const { agents } = useSelector((state) => state.agents || {});
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    agent: "",
    code: "",
    no_of_usage: "",
    min_price: "",
    reward_point: "",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    dispatch(AgentList({
      page: 1,
      limit: 1000,
      is_deleted: false,
      role: ["exclusive partner"],
    }));
  }, [dispatch]);

  const clearError = (field) => {
    setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "code") {
      setFormData((prev) => ({ ...prev, code: value.toUpperCase() }));
      clearError("code");
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleReset = () => {
    setFormData({ agent: "", code: "", no_of_usage: "", min_price: "", reward_point: "", start_date: "", end_date: "" });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.agent) newErrors.agent = "Agent is required";
    if (!formData.code.trim()) newErrors.code = "Code is required";
    if (!formData.no_of_usage) newErrors.no_of_usage = "Number of usage is required";
    if (!formData.min_price) newErrors.min_price = "Minimum price is required";
    if (!formData.reward_point) newErrors.reward_point = "Reward point is required";
    if (!formData.start_date) newErrors.start_date = "Start date is required";
    if (!formData.end_date) newErrors.end_date = "End date is required";
    if (formData.start_date && formData.end_date && formData.end_date <= formData.start_date) {
      newErrors.end_date = "End date must be after start date";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload = {
        agent: formData.agent,
        code: formData.code,
        no_of_usage: Number(formData.no_of_usage),
        min_price: Number(formData.min_price),
        reward_point: Number(formData.reward_point),
        start_date: formData.start_date,
        end_date: formData.end_date,
      };

      const response = await dispatch(addNewRewardCoupon(payload));
      if (response?.statusCode === 200) {
        toast.success("Reward coupon added successfully!");
        handleReset();
        navigate("/dealsAndOffers");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Failed to add reward coupon";
      if (msg.toLowerCase().includes("duplicate") || msg.toLowerCase().includes("already")) {
        setErrors((prev) => ({ ...prev, code: "Coupon code already exists" }));
        return toast.error("Coupon code already exists");
      }
      toast.error(msg);
    }
  };

  return (
    <MainLayout>
      <div className="arc-wrapper">
        <div className="arc-header">
          <div className="arc-header-left">
            <div className="arc-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 12V22H4V12" /><path d="M22 7H2v5h20V7z" /><path d="M12 22V7" />
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
              </svg>
            </div>
            <div>
              <h1 className="arc-title">Add Reward Coupon</h1>
              <p className="arc-subtitle">Fill in the details to create a new reward coupon</p>
            </div>
          </div>
          <Link to="/dealsAndOffers" className="arc-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 12V22H4V12" /><path d="M22 7H2v5h20V7z" />
            </svg>
            View Coupons
          </Link>
        </div>

        <form className="arc-form" onSubmit={handleSubmit}>
          <div className="arc-section arc-section--visible">
            <div className="arc-section-header">
              <span className="arc-section-badge">01</span>
              <h2 className="arc-section-title">Coupon Details</h2>
            </div>

            <div className="arc-grid arc-grid-2">

              <div className="arc-field arc-field--full">
                <label className="arc-label">Agent <span className="arc-req">*</span></label>
                <select
                  className={`arc-select ${errors.agent ? "arc-input--error" : ""}`}
                  name="agent"
                  value={formData.agent}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select agent</option>
                  {Array.isArray(agents?.data) && agents.data.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.name} {agent.primary_contact_number ? `— ${agent.primary_contact_number}` : ""}
                    </option>
                  ))}
                </select>
                {errors.agent && <span className="arc-error-msg">{errors.agent}</span>}
              </div>

              <div className="arc-field arc-field--full">
                <label className="arc-label">Coupon Code <span className="arc-req">*</span></label>
                <input
                  className={`arc-input ${errors.code ? "arc-input--error" : ""}`}
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="e.g. REWARD50"
                  disabled={loading}
                />
                {errors.code && <span className="arc-error-msg">{errors.code}</span>}
              </div>

              <div className="arc-field">
                <label className="arc-label">No of Usage <span className="arc-req">*</span></label>
                <input
                  className={`arc-input ${errors.no_of_usage ? "arc-input--error" : ""}`}
                  type="number"
                  name="no_of_usage"
                  value={formData.no_of_usage}
                  onWheel={(e) => e.target.blur()}
                  onChange={handleChange}
                  placeholder="e.g. 100"
                  disabled={loading}
                  min={1}
                />
                {errors.no_of_usage && <span className="arc-error-msg">{errors.no_of_usage}</span>}
              </div>

              <div className="arc-field">
                <label className="arc-label">Minimum Price <span className="arc-req">*</span></label>
                <input
                  className={`arc-input ${errors.min_price ? "arc-input--error" : ""}`}
                  type="number"
                  name="min_price"
                  value={formData.min_price}
                  onWheel={(e) => e.target.blur()}
                  onChange={handleChange}
                  placeholder="e.g. 500"
                  disabled={loading}
                  min={0}
                />
                {errors.min_price && <span className="arc-error-msg">{errors.min_price}</span>}
              </div>

              <div className="arc-field">
                <label className="arc-label">Reward Points <span className="arc-req">*</span></label>
                <input
                  className={`arc-input ${errors.reward_point ? "arc-input--error" : ""}`}
                  type="number"
                  name="reward_point"
                  value={formData.reward_point}
                  onWheel={(e) => e.target.blur()}
                  onChange={handleChange}
                  placeholder="e.g. 50"
                  disabled={loading}
                  min={0}
                />
                {errors.reward_point && <span className="arc-error-msg">{errors.reward_point}</span>}
              </div>

              <div className="arc-field">
                <label className="arc-label">Start Date <span className="arc-req">*</span></label>
                <input
                  className={`arc-input ${errors.start_date ? "arc-input--error" : ""}`}
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  disabled={loading}
                />
                {errors.start_date && <span className="arc-error-msg">{errors.start_date}</span>}
              </div>

              <div className="arc-field">
                <label className="arc-label">End Date <span className="arc-req">*</span></label>
                <input
                  className={`arc-input ${errors.end_date ? "arc-input--error" : ""}`}
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  disabled={loading}
                />
                {errors.end_date && <span className="arc-error-msg">{errors.end_date}</span>}
              </div>

            </div>
          </div>

          <div className="arc-footer">
            <div className="arc-footer-nav">
              <button type="submit" className="arc-btn arc-btn--submit" disabled={loading}>
                {loading ? "Saving..." : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Save Coupon
                  </>
                )}
              </button>
            </div>
            <Link to="/dealsAndOffers" className="arc-btn arc-btn--cancel" onClick={handleReset}>Cancel</Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddRewardCoupon;